/**
 * Silent JWT refresh at 50 % of the session window (server-relative), with `lines_up` so the
 * api rotates the SIP password only while no line is up (80 % rule lives server side; the
 * phone only ever applies a new credential with zero lines up). Failures retry every 30 s
 * until the JWT expires, then the re-launch card is shown.
 */
import { z } from 'zod';
import { RefreshResponse, type RefreshRequest } from '@ccphone/contracts';
import type { Clock } from './clock.js';
import type { ApiClient } from './api.js';
import { ApiError } from './api.js';
import type { Logger } from '../log.js';
import { DeadlineTimer, type TimerHost, realTimers } from '../keepalive/timers.js';

export type RefreshResult = z.infer<typeof RefreshResponse>;

export interface SessionRefresherDeps {
  api: ApiClient;
  clock: Clock;
  timers?: TimerHost;
  log: Logger;
  getRefreshToken(): string;
  getExpiresAt(): string;
  getIssuedAt(): string;
  linesUp(): number;
  onRefreshed(r: RefreshResult): void;
  /** the session cannot be refreshed any more: show the re-launch card */
  onExpired(reason: string): void;
}

export const REFRESH_RETRY_MS = 30_000;

/** the 50 % point between issue and expiry, server-relative */
export function refreshAt(issuedAtIso: string, expiresAtIso: string): number {
  const a = Date.parse(issuedAtIso);
  const b = Date.parse(expiresAtIso);
  return a + (b - a) / 2;
}

export class SessionRefresher {
  private readonly timer: DeadlineTimer;
  private inflight = false;
  private stopped = false;

  constructor(private readonly d: SessionRefresherDeps) {
    this.timer = new DeadlineTimer(d.clock, d.timers ?? realTimers);
  }

  start(): void {
    this.stopped = false;
    this.schedule(refreshAt(this.d.getIssuedAt(), this.d.getExpiresAt()));
  }

  stop(): void {
    this.stopped = true;
    this.timer.cancel();
  }

  private schedule(at: number): void {
    if (this.stopped) return;
    this.timer.arm(at, () => void this.refreshNow());
  }

  async refreshNow(): Promise<boolean> {
    if (this.inflight || this.stopped) return false;
    this.inflight = true;
    try {
      const body: RefreshRequest = { refresh_token: this.d.getRefreshToken(), lines_up: this.d.linesUp() };
      const r = await this.d.api.request('POST', '/v1/session/refresh', RefreshResponse, body, { auth: false });
      this.d.log.info({ rotated_sip: Boolean(r.sip) }, 'session refreshed');
      this.d.onRefreshed(r);
      // the new window starts at the server time of the response
      this.schedule(refreshAt(r.server_time, r.expires_at));
      return true;
    } catch (e) {
      const terminal = e instanceof ApiError && (e.status === 401 || e.status === 403 || e.status === 404 || e.status === 410);
      const expired = this.d.clock.now() >= Date.parse(this.d.getExpiresAt());
      if (terminal || expired) {
        this.d.log.warn({ err: (e as Error).message, terminal, expired }, 'session refresh failed for good');
        this.timer.cancel();
        this.d.onExpired(terminal ? 'refresh_rejected' : 'expired');
      } else {
        this.d.log.warn({ err: (e as Error).message }, 'session refresh failed; retrying');
        this.schedule(this.d.clock.now() + REFRESH_RETRY_MS);
      }
      return false;
    } finally {
      this.inflight = false;
    }
  }
}
